import React, { useState } from 'react';
import classnames from 'classnames';

// Types
import { GoalItem } from 'Shared/constants/types';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Utils
import { isEmpty, editorStateToJsonString } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpRichTextInput } from 'Wpds/components/rich-text';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';

// Styles
import './GoalDescription.scss';

type Props = {
  goal: GoalItem;
  editable?: boolean;
};

const GoalDescription = ({ goal, editable = false }: Props) => {
  const { tr } = useTranslation();
  const { canChangeStatus, isSaving, handleUpdateGoal } = useGoal({ slug: goal.slug });

  const [value, setValue] = useState(goal.description);
  const [isEditing, toggleIsEditing] = useToggle(false);

  const canUpdate = canChangeStatus && editable;

  if (isEmpty(goal.description) && !canUpdate) return null;

  const baseClassName = 'GoalDescription';
  const classes = classnames(baseClassName, canUpdate ? `${baseClassName}--editable` : '');

  const handleCancelClick = () => {
    setValue(goal.description);
    toggleIsEditing();
  };

  const handleSaveClick = () => {
    handleUpdateGoal({ description: editorStateToJsonString(value) }).then(() => {
      toggleIsEditing();
    });
  };

  if (isEditing) {
    return (
      <WpContainer className={baseClassName}>
        <WpContainerBody padding="05">
          <WpRichTextInput
            value={value}
            options={{ showBorder: false, backgroundColor: 'transparent' }}
            placeholder={tr('perform.goals.description.add_placeholder')}
            autoFocus
            onChange={setValue}
          />
          <WpButtonGroup spacing="md">
            <WpButton size="xs" square slimPadding onClick={handleSaveClick} loading={isSaving}>
              {tr('perform.goals.profile.save_button')}
            </WpButton>
            <WpButton variant="tertiary" size="xs" square slimPadding onClick={handleCancelClick} disabled={isSaving}>
              {tr('perform.goals.profile.cancel_button')}
            </WpButton>
          </WpButtonGroup>
        </WpContainerBody>
      </WpContainer>
    );
  }
  return (
    <div className={classes} onClick={canUpdate && toggleIsEditing} role={canUpdate ? 'button' : null}>
      <WpRichTextInput
        value={goal.description}
        readOnly
        options={{ showBorder: false, backgroundColor: 'transparent' }}
        height="auto"
        placeholder={tr('perform.goals.description.add_placeholder')}
      />
    </div>
  );
};

export default GoalDescription;
